import {
  type ClockChangeError,
  type ClockSchedule,
  clockChangeDay,
  clockOffsetAt,
  clockSchedule,
  formatUtcOffset,
  validateClockChange,
  wallInstants,
  wallLabel,
} from '@felix/domain';
import { type ClockChangeView, clockText, daySpan } from '../personnel/ship-clock.js';

/**
 * Gemi saati kaydı ekranının saf yardımcıları (ADR 0065): taslak değişikliğin anı, eski ve yeni
 * farkı, önizleme ve denetim; liste biçimleri.
 */

const MINUTE = 60_000;

/** `−60 dk` biçimi: eksi U+2212, artı açık. */
export function signedMinutes(minutes: number): string {
  return `${minutes < 0 ? '−' : '+'}${Math.abs(minutes)}`;
}

export { formatUtcOffset };

/**
 * Plan çizelgesi: iptal edilmemiş bütün kayıtlar (onay bekleyenler dahil) zincirdir; yeni
 * kaydın eski farkı ve anı bu zincire göre bulunur (sunucunun `chainOffsetAt` kuralı).
 */
export function planSchedule(timeZone: string, changes: readonly ClockChangeView[]): ClockSchedule {
  return clockSchedule(
    timeZone,
    changes.filter((c) => c.status !== 'cancelled'),
  );
}

export interface ClockDraftInput {
  /** Gemi saatiyle gün ve değişiklikten önceki saat. */
  date: string;
  time: string;
  direction: 'retard' | 'advance';
  minutes: number;
  reason: string;
}

export type ClockDraft =
  | {
      ok: true;
      effectiveAt: string;
      oldOffsetMin: number;
      newOffsetMin: number;
      changeMin: number;
      /** Değişiklikten sonra saatin gösterdiği. */
      after: string;
      /** Değişikliğin gününün süresi (dakika). */
      dayMinutes: number;
      errors: ClockChangeError[];
    }
  | { ok: false; code: 'INVALID_DATE' | 'TIME_SKIPPED' };

/** Taslak değişiklik: anı plan zincirinden, denetimi domain kuralıyla (ADR 0065 §1). */
export function clockDraft(input: ClockDraftInput, plan: ClockSchedule, now: string): ClockDraft {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(input.time)) {
    return { ok: false, code: 'INVALID_DATE' };
  }
  const minute = Number(input.time.slice(0, 2)) * 60 + Number(input.time.slice(3, 5));
  // Tekrar eden saatte ikinci oluşum: zincirin ucundaki değişiklikten sonraki an
  const at = wallInstants(plan, input.date, minute).at(-1);
  if (at === undefined) return { ok: false, code: 'TIME_SKIPPED' };
  const oldOffsetMin = clockOffsetAt(plan, at);
  const changeMin = (input.direction === 'retard' ? -1 : 1) * Math.round(input.minutes);
  const newOffsetMin = oldOffsetMin + changeMin;
  const effectiveAt = new Date(at).toISOString();
  const errors = validateClockChange(
    { effectiveAt, oldOffsetMin, newOffsetMin, changeMin, reason: input.reason },
    now,
  );
  const withChange = clockSchedule(plan.timeZone, [
    ...plan.changes.map((s) => ({
      effectiveAt: new Date(s.at).toISOString(),
      oldOffsetMin: s.old,
      newOffsetMin: s.next,
    })),
    { effectiveAt, oldOffsetMin, newOffsetMin },
  ]);
  const day = clockChangeDay(withChange, { at });
  const after = wallLabel(withChange, at);
  return {
    ok: true,
    effectiveAt,
    oldOffsetMin,
    newOffsetMin,
    changeMin,
    after: clockText(after.minute),
    dayMinutes: daySpan(withChange, day).minutes,
    errors,
  };
}

/** Değişikliğin gemi saatiyle (eski saat) gösterimi: `GG.AA.YYYY SS:DD`. */
export function effectiveText(change: Pick<ClockChangeView, 'effectiveAt' | 'oldOffsetMin'>) {
  const wall = new Date(
    Date.parse(change.effectiveAt) + change.oldOffsetMin * MINUTE,
  ).toISOString();
  return `${wall.slice(8, 10)}.${wall.slice(5, 7)}.${wall.slice(0, 4)} ${wall.slice(11, 16)}`;
}

/** UTC an: `GG.AA.YYYY SS:DD UTC`. */
export function utcText(at: string): string {
  return `${at.slice(8, 10)}.${at.slice(5, 7)}.${at.slice(0, 4)} ${at.slice(11, 16)} UTC`;
}

/** Saat süresi: 1500 → "25 h", 1460 → "24 h 20 min" yerine "24:20". */
export function hoursText(minutes: number): string {
  if (minutes % 60 === 0) return `${minutes / 60} h`;
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')} h`;
}

/** Değişikliğin gününün süresi (onaylı zincirle; ekranda rozet). */
export function changeDayMinutes(
  timeZone: string,
  changes: readonly ClockChangeView[],
  change: ClockChangeView,
) {
  const plan = planSchedule(timeZone, changes);
  const day = clockChangeDay(plan, { at: Date.parse(change.effectiveAt) });
  return daySpan(plan, day).minutes;
}

/** Kişi bu kaydı iptal edebilir mi: yalnız zincirin son kaydı, yürürlükte değilse (§1). */
export function cancellable(
  data: {
    changes: readonly ClockChangeView[];
    now: string;
    userId: string | null;
    can: { void: boolean; voidOwn: boolean };
  },
  change: ClockChangeView,
): boolean {
  if (change.status === 'cancelled') return false;
  const live = data.changes.filter((c) => c.status !== 'cancelled');
  if (live.at(-1)?.id !== change.id) return false;
  if (change.status === 'approved' && Date.parse(change.effectiveAt) <= Date.parse(data.now)) {
    return false;
  }
  return data.can.void || (data.can.voidOwn && change.createdBy === data.userId);
}

/** Onaylanabilir mi: bekleyen ve önceki bütün kayıtlar onaylı (sırayla onay; §1). */
export function approvable(changes: readonly ClockChangeView[], change: ClockChangeView): boolean {
  if (change.status !== 'pending') return false;
  return !changes.some(
    (c) => c.status === 'pending' && Date.parse(c.effectiveAt) < Date.parse(change.effectiveAt),
  );
}
